import { Box, Stack, Typography } from '@mui/material'
import {
  splitValvesAndPumps,
  type FlowRuleChildren,
} from '../../hooks/hydraulic/flowRuleCascade'

/**
 * SCRUM-1032 — what else a delete is about to take with it.
 *
 * Deleting a flow rule (or the pipe holding one) cannot leave its valves and
 * sensors behind: the API refuses, so the app removes them too. That is a
 * bigger action than the user asked for, so it is spelled out before they
 * agree rather than reported afterwards.
 */

interface CascadeDeleteNoticeProps {
  children: FlowRuleChildren
  /** Named when the pipe's own rule is going as well as its contents. */
  includesFlowRule?: boolean
  isLoading?: boolean
}

const CascadeDeleteNotice = ({
  children,
  includesFlowRule = false,
  isLoading = false,
}: CascadeDeleteNoticeProps) => {
  const plural = (count: number, word: string) =>
    `${count} ${word}${count === 1 ? '' : 's'}`

  // Counted apart: they share a column, but losing a pump is not the same as
  // losing a valve, and "1 valve or pump" left the user to go and look.
  const { valves, pumps } = splitValvesAndPumps(children.valves)

  const items: string[] = []
  if (includesFlowRule) items.push('Its flow rule')
  if (valves) items.push(plural(valves, 'valve'))
  if (pumps) items.push(plural(pumps, 'pump'))
  if (children.sensors.length) {
    items.push(plural(children.sensors.length, 'flow sensor'))
  }

  if (isLoading) {
    return (
      <Typography sx={{ fontSize: '12px', color: 'grey.600', maxWidth: '314px' }}>
        Checking what else this would remove…
      </Typography>
    )
  }

  if (items.length === 0) return null

  return (
    <Stack
      spacing={0.75}
      sx={{
        maxWidth: '314px',
        mt: '4px',
        p: '10px 12px',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(253,162,155,0.4)' : '#F3C2C5',
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(253,162,155,0.08)'
            : 'rgba(180,35,24,0.05)',
      }}
    >
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          color: (theme) => (theme.palette.mode === 'dark' ? '#FDA29B' : '#B42318'),
        }}
      >
        This will also be deleted
      </Typography>
      <Stack component="ul" sx={{ m: 0, pl: '16px', gap: '2px' }}>
        {items.map((item) => (
          <Box
            key={item}
            component="li"
            sx={{ fontSize: '12px', lineHeight: 1.35, color: 'text.main' }}
          >
            {item}
          </Box>
        ))}
      </Stack>
    </Stack>
  )
}

export default CascadeDeleteNotice
